// ===============================================
// File: src/contexts/MaterialContext.jsx
// Persistence: Unified (Folder or Drive) via StorageContext.
// Writes to domain "materials_domain" and rehydrates on boot + after Receive.
// ===============================================

import React, {
  createContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useContext,
} from "react";
import { useStorage } from "@/contexts/StorageContext";

/* ---------- helpers ---------- */
const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const getStock = (m) =>
  toNum(m?.stock ?? m?.stockQty ?? m?.quantity ?? m?.startQuantity ?? 0);

const coerceList = (v) => (Array.isArray(v) ? v : []);

/* ---------- context shape ---------- */
export const MaterialContext = createContext({
  materials: [],
  scrapedMaterials: [],
  addMaterial: () => {},
  updateMaterial: () => {},
  deleteMaterial: () => {},
  adjustStock: () => {},
  bulkReceive: () => {},
});

/* ---------- provider ---------- */
export function MaterialProvider({ children }) {
  // Unified domain helpers (StorageContext exposes readDomain/writeDomain)
  const { readDomain, writeDomain } = useStorage();

  // IMPORTANT: storage domain key as defined in StorageContext.DOMAIN_FILES
  const DOMAIN_KEY = "materials_domain";

  // keep the IO funcs stable across effects
  const readRef = useRef(readDomain);
  const writeRef = useRef(writeDomain);
  useEffect(() => { readRef.current = readDomain; }, [readDomain]);
  useEffect(() => { writeRef.current = writeDomain; }, [writeDomain]);

  const loadedRef = useRef(false);
  const saveDebounce = useRef(0);

  const [materials, setMaterials] = useState([]);
  const [scrapedMaterials] = useState([]); // reserved for future import/scrape flows

  // ----- initial load / hydrate from Folder or Drive -----
  useEffect(() => {
    let alive = true;
    loadedRef.current = false;

    (async () => {
      try {
        const data = await readRef.current(DOMAIN_KEY, []);
        if (alive) setMaterials(coerceList(data));
      } catch {
        if (alive) setMaterials([]);
      } finally {
        loadedRef.current = true;
      }
    })();

    return () => { alive = false; };
  }, []); // IO handled via refs

  // ----- rehydrate after Receive (vault or files) -----
  useEffect(() => {
    const onRestored = async () => {
      try {
        const data = await readRef.current(DOMAIN_KEY, []);
        setMaterials(coerceList(data));
      } catch {
        // ignore
      }
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      setMaterials([]);
      loadedRef.current = false;
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, []);

  // ----- persist whenever materials change (after initial load, debounced) -----
  useEffect(() => {
    if (!loadedRef.current) return;
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      try {
        await writeRef.current(DOMAIN_KEY, materials);
      } catch {
        // swallow to avoid UI noise; caller may show toasts elsewhere
      }
    }, 250);
    return () => clearTimeout(saveDebounce.current);
  }, [materials]);

  /* ---------- CRUD ---------- */
  const addMaterial = useCallback((mat) => {
    const id = mat?.id || (crypto?.randomUUID?.() ?? String(Date.now()));
    setMaterials((prevRaw) => {
      const prev = Array.isArray(prevRaw) ? prevRaw : [];
      const base = {
        ...mat,
        id,
        stock: getStock(mat),
        stockQty: getStock(mat),
        startQuantity: getStock(mat),
        createdAt: mat?.createdAt ?? Date.now(),
        updatedAt: Date.now(),
      };
      // put newest first
      return [base, ...prev];
    });
  }, []);

  const updateMaterial = useCallback((idOrObj, maybePatch) => {
    let id = idOrObj;
    let patch = maybePatch;
    if (typeof idOrObj === "object" && idOrObj !== null && maybePatch === undefined) {
      id = idOrObj.id;
      patch = idOrObj;
    }
    if (!id) return;

    setMaterials((prevRaw) => {
      const prev = Array.isArray(prevRaw) ? prevRaw : [];
      return prev.map((m) => {
        if (String(m.id) !== String(id)) return m;
        const nextMixed = { ...m, ...patch };
        const nextStock = getStock(nextMixed);
        return {
          ...nextMixed,
          stock: nextStock,
          stockQty: nextStock,
          startQuantity: nextStock,
          updatedAt: Date.now(),
        };
      });
    });
  }, []);

  const deleteMaterial = useCallback((id) => {
    setMaterials((prevRaw) => {
      const prev = Array.isArray(prevRaw) ? prevRaw : [];
      return prev.filter((m) => String(m.id) !== String(id));
    });
  }, []);

  const adjustStock = useCallback((id, delta) => {
    setMaterials((prevRaw) => {
      const prev = Array.isArray(prevRaw) ? prevRaw : [];
      return prev.map((m) => {
        if (String(m.id) !== String(id)) return m;
        const next = getStock(m) + toNum(delta);
        return {
          ...m,
          stock: next,
          stockQty: next,
          startQuantity: next,
          updatedAt: Date.now(),
        };
      });
    });
  }, []);

  const bulkReceive = useCallback((items) => {
    if (!Array.isArray(items) || !items.length) return;
    const map = new Map();
    items.forEach((it) => {
      const id = it.materialId ?? it.id;
      const delta = toNum(it.qty);
      if (id && delta) map.set(String(id), (map.get(String(id)) || 0) + delta);
    });

    setMaterials((prevRaw) => {
      const prev = Array.isArray(prevRaw) ? prevRaw : [];
      return prev.map((m) => {
        const add = map.get(String(m.id));
        if (!add) return m;
        const next = getStock(m) + add;
        return {
          ...m,
          stock: next,
          stockQty: next,
          startQuantity: next,
          updatedAt: Date.now(),
        };
      });
    });
  }, []);

  /* ---------- context value ---------- */
  const value = useMemo(
    () => ({
      materials,
      scrapedMaterials,
      addMaterial,
      updateMaterial,
      deleteMaterial,
      adjustStock,
      bulkReceive,
    }),
    [materials, scrapedMaterials, addMaterial, updateMaterial, deleteMaterial, adjustStock, bulkReceive]
  );

  return <MaterialContext.Provider value={value}>{children}</MaterialContext.Provider>;
}

export const useMaterials = () => useContext(MaterialContext);
